import { ApolloQueryResult } from 'apollo-client';
import { BaseService } from './BaseService';
import { FetchResult } from 'apollo-link';
import ScopedListInquiriesQuery from '@/graphql/queries/ScopedListInquiries.gql';
import acceptInquiryQuery from '@/graphql/mutations/AcceptInquiry.gql';
import deleteInquiry from '@/graphql/mutations/InquiryDelete.gql';
import deleteQuotesMutation from '@/graphql/mutations/DeleteQuote.gql';
import fetchInquiryQuery from '@/graphql/queries/FetchInquiry.gql';
import fetchQuotesQuery from '@/graphql/queries/FetchQuotes.gql';
import listInquiriesQuery from '@/graphql/queries/ListInquiries.gql';
import quoteSelect from '@/graphql/mutations/QuoteSelect.gql';
import quoteSubmit from '@/graphql/mutations/QuoteSubmit.gql';
import simulateQuoteMutation from '@/graphql/mutations/SimulateQuote.gql';
import updateInquiry from '@/graphql/mutations/UpdateInquiry.gql';
import upsertQuoteMutation from '@/graphql/mutations/UpsertQuote.gql';
import store from '@/stores/index';
import {
  InquiryInput,
  InquiryScopedInput,
  PaginationInput,
  QuoteInput,
  QuoteSubmit,
  ScopedListInquiries,
  UpdateInquiry,
  acceptInquiry,
  fetchInquiry,
  fetchQuotes,
  listInquiries,
  upsertQuote,
  upsertQuoteVariables,
  deleteQuoteVariables,
  deleteQuote,
  QuoteSectionEnum,
  InquiryStatusEnum
} from '@/types/graphql/schemaTypes';

class InquiryService extends BaseService {
  public acceptInquiry(inquiryArgs: InquiryScopedInput):
    Promise<FetchResult<acceptInquiry>> {
    return this.client.mutate({
      mutation: acceptInquiryQuery,
      variables: {
        inquiryArgs
      }
    });
  }

  public deleteInquiry(inquiryArgs: InquiryInput): Promise<FetchResult<boolean>> {
    return this.client.mutate({
      mutation: deleteInquiry,
      variables: {
        inquiryArgs
      }
    });
  }

  public unarchiveInquiry(inquiryId: string):
  Promise<FetchResult<UpdateInquiry>> {
    return this.client.mutate({
      mutation: updateInquiry,
      variables: {
        inquiryArgs: {
          id: inquiryId,
          status: InquiryStatusEnum.inquiry_created
        }
      }
    }).catch((error) => {
      store.commit('Alert/addAlert', { type: 'error', message: error.message });
      return error;
    });
  }

  public fetchInquiry(inquiryArgs: InquiryScopedInput): Promise<ApolloQueryResult<fetchInquiry>> {
    return this.client.query({
      query: fetchInquiryQuery,
      variables: {
        inquiryArgs
      }
    }).then((result: ApolloQueryResult<any>) => {
      return result;
    });
  }

  public listInquiries(): Promise<ApolloQueryResult<listInquiries>> {
    return this.client.query({
      query: listInquiriesQuery
    }).then((result: ApolloQueryResult<any>) => {
      return result;
    });
  }

  public scopedListInquiries(inquiryArgs: InquiryScopedInput, pagination: PaginationInput):
    Promise<ApolloQueryResult<ScopedListInquiries>> {
    return this.client.query({
      query: ScopedListInquiriesQuery,
      variables: {
        inquiryArgs,
        pagination
      }
    }).then((result: ApolloQueryResult<any>) => {
      return result;
    });
  }

  public updateInquiry(inquiryArgs: InquiryInput):
    Promise<FetchResult<UpdateInquiry>> {
    return this.client.mutate({
      mutation: updateInquiry,
      variables: {
        inquiryArgs
      }
    });
  }

  // Quote related
  public deleteQuote(quoteId: deleteQuoteVariables): Promise<FetchResult<deleteQuote>> {
    return this.client.mutate({
      mutation: deleteQuotesMutation,
      variables: {
        quoteId
      }
    });
  }

  public fetchQuotes(inquiryArgs: InquiryScopedInput): Promise<ApolloQueryResult<fetchQuotes>> {
    return this.client.query({
      query: fetchQuotesQuery,
      fetchPolicy: 'no-cache',
      variables: {
        inquiryArgs
      }
    });
  }

  public quoteSubmit(quoteArgs: QuoteInput): Promise<FetchResult<QuoteSubmit>> {
    return this.client.mutate({
      mutation: quoteSubmit,
      variables: {
        quoteArgs
      }
    });
  }

  public selectQuote(quoteId: string, selectedSections: QuoteSectionEnum[]): Promise<FetchResult<boolean>> {
    return this.client.mutate({
      mutation: quoteSelect,
      variables: {
        quoteId,
        selectedSections
      }
    });
  }

  /**
   * @param upsertQuoteArgs object with :
   * - shipmentId: string
   * - quoteId?: string
   * - inquiryArgs?: InquiryInput
   * - extraBillItems?: BillItemInput[]
   * - margin?: number
   */
  public upsertQuote(upsertQuoteArgs: upsertQuoteVariables): Promise<FetchResult<upsertQuote>> {
    return this.client.mutate({
      mutation: upsertQuoteMutation,
      variables: {
        inquiryArgs: upsertQuoteArgs.inquiryArgs,
        margin: upsertQuoteArgs.margin,
        quoteId: upsertQuoteArgs.quoteId,
        extraBillItems: upsertQuoteArgs.extraBillItems || [],
        shipmentId: upsertQuoteArgs.shipmentId
      }
    });
  }

  /**
   * @param upsertQuoteArgs object with :
   * - shipmentId: string
   * - quoteId?: string
   * - inquiryArgs?: InquiryInput
   * - extraBillItems?: BillItemInput[]
   * - margin?: number
   */
  public simulateQuote(upsertQuoteArgs: upsertQuoteVariables): Promise<FetchResult<upsertQuote>> {
    return this.client.mutate({
      mutation: simulateQuoteMutation,
      variables: {
        inquiryArgs: upsertQuoteArgs.inquiryArgs,
        margin: upsertQuoteArgs.margin,
        quoteId: upsertQuoteArgs.quoteId,
        extraBillItems: upsertQuoteArgs.extraBillItems || [],
        shipmentId: upsertQuoteArgs.shipmentId
      }
    });
  }
}

export const inquiryService = new InquiryService();
